import { Component, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import {
  ApiSubscriptionDetails,
  ApiSubscriptionsService,
  UpdateApiSubscriptionRequest,
} from 'src/app/services/api-subscriptions.service';
import { Tenant } from 'src/app/services/tenants.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-edit-api-subscription',
  templateUrl: './edit-api-subscription.component.html',
  styleUrl: './edit-api-subscription.component.scss',
})
export class EditApiSubscriptionComponent {
  @ViewChild('deleteSubscriptionDialog')
  deleteSubscriptionDialog!: ElementRef<HTMLDialogElement>;

  loadingSubscription = false;
  errorFetchingSubscription = '';
  originalSubscription?: ApiSubscriptionDetails;
  tenant?: Tenant;

  inputStartDate? = '';
  inputNotes = '';
  active: boolean = true;

  deletingSubscription = false;

  updating = false;
  errorUpdating = '';

  constructor(
    private apiSubscriptionsService: ApiSubscriptionsService,
    public location: Location,
    private route: ActivatedRoute,
    private toastService: ToastService
  ) {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      console.error(new Error('No subscription ID found in route'));
      this.errorFetchingSubscription = 'No subscription ID found';
      return;
    }
    this.getAPISubscription(id);
  }

  get hasChanges() {
    const sub = this.originalSubscription?.api_subscription;
    if (!sub) {
      return false;
    }
    return (
      this.inputStartDate !== sub.start_date.slice(0, 10) ||
      this.active !== sub.active ||
      this.inputNotes !== (sub.notes ?? '')
    );
  }

  getAPISubscription(id: string) {
    this.loadingSubscription = true;
    this.apiSubscriptionsService.getApiSubscriptionDetails(id).subscribe({
      next: (subscription) => {
        console.log('subscription: ', subscription);
        this.loadingSubscription = false;
        this.originalSubscription = subscription;
        this.tenant = subscription.tenant;
        this.inputStartDate = subscription.api_subscription.start_date.slice(
          0,
          10
        );
        this.active = subscription.api_subscription.active;
        this.inputNotes = subscription.api_subscription.notes ?? '';
      },
      error: (err) => {
        this.loadingSubscription = false;
        console.error(err);
        this.errorFetchingSubscription = err.message;
      },
    });
  }

  showConfirmDeleteDialog() {
    this.deleteSubscriptionDialog.nativeElement.showModal();
  }

  closeDeleteSubscriptionDialog() {
    this.deleteSubscriptionDialog.nativeElement.close();
  }

  updateSubscription() {
    if (!this.originalSubscription) {
      this.errorUpdating = 'Error updating subscription. No basis for update.';
      return;
    }

    if (!this.inputStartDate) {
      this.errorUpdating = 'Please enter a start date';
      return;
    }

    this.updating = true;
    this.errorUpdating = '';

    const updates: UpdateApiSubscriptionRequest = {
      id: this.originalSubscription.api_subscription.id,
      start_date: new Date(this.inputStartDate),
      active: this.active,
    };
    const origNotes = this.originalSubscription.api_subscription.notes ?? '';
    if (this.inputNotes !== origNotes) {
      updates.notes = this.inputNotes;
    }

    this.apiSubscriptionsService
      .updateApiSubscription(
        this.originalSubscription.api_subscription.id,
        updates
      )
      .subscribe({
        next: (response) => {
          this.updating = false;
          this.errorUpdating = '';
          this.originalSubscription!.api_subscription = response;
          this.inputNotes = response.notes ?? '';
          this.toastService.addToast(
            'success',
            'Subscription updated successfully'
          );
        },
        error: (error) => {
          this.updating = false;
          this.errorUpdating = error.message;
        },
      });
  }

  deleteSubscription() {
    if (!this.originalSubscription) {
      this.errorUpdating =
        'Error deleting subscription. No basis for deletion.';
      return;
    }

    this.deletingSubscription = true;
    this.apiSubscriptionsService
      .deleteApiSubscription(this.originalSubscription!.api_subscription.id)
      .subscribe({
        next: (response) => {
          this.deletingSubscription = false;
          // close dialog and go back
          this.closeDeleteSubscriptionDialog();
          this.location.back();

          this.toastService.addToast(
            'success',
            'Subscription deleted successfully'
          );
        },
        error: (error) => {
          this.deletingSubscription = false;
          this.errorUpdating = error.message;
        },
      });
  }
}
